/**
 * BloodTrendChart — как се движат хемоглобинът, ферритинът, сатурацията
 * и MCV през времето. Ферритинът пада месеци преди хемоглобина, а след
 * добавка желязо първо се качва ферритинът.
 * Данни: GET /api/hematology-twin/trends
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  LineChart, Line, ResponsiveContainer, CartesianGrid, XAxis, YAxis, Tooltip,
} from "recharts";
import { TrendingUp, TrendingDown, Minus } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ICON = { "нагоре": TrendingUp, "надолу": TrendingDown, "без промяна": Minus };

export default function BloodTrendChart({ isDark = false, cardCls = "" }) {
  const [d, setD] = useState(null);
  const [active, setActive] = useState(null);

  useEffect(() => {
    axios.get(`${API}/hematology-twin/trends`).then((r) => {
      setD(r.data);
      setActive((r.data.series || [])[0]?.marker || null);
    }).catch(() => {});
  }, []);

  if (!d || !d.series?.length) return null;
  const border = isDark ? "border-white/10" : "border-gray-200";
  const s = d.series.find((x) => x.marker === active) || d.series[0];
  const Icon = ICON[s.direction] || Minus;

  return (
    <div className={cardCls || `p-4 rounded-2xl border ${border}`} data-testid="blood-trend-card">
      <div className="text-sm font-bold">Посока на кръвните показатели</div>
      <div className="mt-2 flex flex-wrap gap-1.5">
        {d.series.map((x) => (
          <button key={x.marker} type="button" onClick={() => setActive(x.marker)}
            className={`px-2 py-1 rounded-lg text-[11px] font-bold border transition-colors ${
              x.marker === s.marker ? "text-white" : border}`}
            style={x.marker === s.marker
              ? { background: x.color, borderColor: x.color } : {}}
            data-testid={`blood-trend-tab-${x.marker}`}>
            {x.label_bg}
          </button>
        ))}
      </div>

      <div className="mt-2 flex items-center gap-2 text-[12px]" data-testid="blood-trend-summary">
        <Icon className="w-4 h-4" style={{ color: s.color }} />
        <span className="font-bold tabular-nums" style={{ color: s.color }}>
          {s.last} {s.unit}
        </span>
        <span className="opacity-70">
          от {s.first} {s.unit} · {s.direction}
          {s.delta ? ` (${s.delta > 0 ? "+" : ""}${s.delta})` : ""} · {s.points.length} измервания
        </span>
      </div>

      <div className="mt-3 h-44" data-testid={`blood-trend-chart-${s.marker}`}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={s.points} margin={{ top: 5, right: 8, left: -18, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3"
              stroke={isDark ? "rgba(255,255,255,.08)" : "rgba(0,0,0,.06)"} />
            <XAxis dataKey="date" tick={{ fontSize: 9 }} tickMargin={4}
              stroke={isDark ? "#94a3b8" : "#64748b"} />
            <YAxis tick={{ fontSize: 9 }} domain={["auto", "auto"]}
              stroke={isDark ? "#94a3b8" : "#64748b"} />
            <Tooltip contentStyle={{
              background: isDark ? "#0f172a" : "#fff", fontSize: 11,
              borderRadius: 8, border: `1px solid ${s.color}` }}
              formatter={(v) => [`${v} ${s.unit}`, s.label_bg]} />
            <Line type="monotone" dataKey="value" stroke={s.color} strokeWidth={2}
              dot={{ r: 2 }} activeDot={{ r: 4 }} />
          </LineChart>
        </ResponsiveContainer>
      </div>
      <p className="text-[10px] opacity-60 mt-1">{d.note_bg}</p>
    </div>
  );
}
